import React, { ReactElement, useEffect, useMemo, useRef, useState } from "react";
import moment from "moment";
import { IMovie } from "@common/redux/types/movie";
import { ArrowDownIcon } from "@kursk/components/ui/icons";
import { CalendarDatePicker } from "@kursk/components/ui/CalendarDatePicker/CalendarDatePicker";
import { MovieScheduleRow } from "./MovieScheduleRow/MovieScheduleRow";

import styles from "./MovieSchedule.module.scss";
import classNames from "classnames";

interface IProps {
  movie: IMovie;
}

const getInitFilterDates = (schedulesGroups) => {
  if (schedulesGroups?.length > 0) {
    let allDates = [];

    schedulesGroups?.forEach((scheduleGroup) => {
      scheduleGroup.schedules.forEach((schedule) => allDates.push(schedule.start));
    });

    const today = moment().startOf('day').valueOf();

    allDates = allDates.filter((date) => date >= today).sort((a, b) => a - b);

    const firstDate = allDates[0];

    if (firstDate) {
      return {
        start: moment(firstDate).startOf('day').valueOf(),
        end: moment(firstDate).endOf('day').valueOf()
      };
    } else {
      return {
        start: moment().startOf('day').valueOf(),
        end: moment().endOf('day').valueOf()
      };
    }
  } else {
    return {
      start: moment().startOf('day').valueOf(),
      end: moment().endOf('day').valueOf()
    };
  }
};

export const MovieSchedule = ({ movie }: IProps): ReactElement => {
  const { schedulesGroups } = movie;

  const calendarRef = useRef(null);
  const [filterDates, setFilterDates] = useState(getInitFilterDates(schedulesGroups));

  useEffect(() => {
    setFilterDates(getInitFilterDates(schedulesGroups));
  }, [movie]);

  const filteredSchedulesGroups = useMemo(() => {
    const filteredSchedules = [];

    schedulesGroups?.forEach((scheduleGroup) => {
      const actualSchedules = scheduleGroup.schedules.filter(
        (schedule) =>
          moment(schedule.start).startOf('day').valueOf() === filterDates.start
      );

      if (actualSchedules.length > 0) {
        filteredSchedules.push({
          ...scheduleGroup,
          schedules: actualSchedules,
        });
      }
    });

    return filteredSchedules;
  }, [schedulesGroups, filterDates])

  const enabledDays = useMemo(() => {
    const availableDays = [];
    schedulesGroups?.forEach((scheduleGroup) => {
      scheduleGroup.schedules.forEach((schedule) => {
        if (availableDays.indexOf(schedule.start) === -1) {
          availableDays.push(schedule.start);
        }
      });
    });

    return availableDays.map((day) => moment(day).format('YYYY-MM-DD'));
  }, [schedulesGroups]);

  const getFilterTitle = () => {
    if (filterDates.start === moment(filterDates.end).startOf('day').valueOf()) {
      if (filterDates.start === moment().startOf('day').valueOf()) {
        return 'Сегодня';
      } else if (filterDates.start === moment().add(1, 'day').startOf('day').valueOf()) {
        return 'Завтра';
      } else {
        return moment(filterDates.start).format('D MMMM');
      }
    } else {
      return `${moment(filterDates.start).format('D MMMM')}-${moment(filterDates.start).format('D MMMM')}`;
    }
  };

  const onFilterDatesChange = (date: number) => {
    const momentDate = moment(date);
    setFilterDates({
      start: momentDate.startOf('day').valueOf(),
      end: momentDate.endOf('day').valueOf(),
    })
  }

  return (
    <div className={styles.wrapper}>
      <div className={styles.tableHeader}>
        <div className={styles.tableHeaderTitle}>Расписание</div>
        <CalendarDatePicker
          ref={calendarRef}
          date={filterDates.start}
          minDate={moment().toDate()}
          enabledDays={enabledDays}
          onChangeDate={onFilterDatesChange}
          customInput={({ isOpen }) => (
            <div>
              <div className={styles.tableHeaderCalendarWrap} onClick={(event) => {
                if (isOpen) {
                  calendarRef?.current && calendarRef.current.setOpen(false);
                  event.stopPropagation()
                }
              }}>
                <span className={styles.tableHeaderCalendarTitle}>{getFilterTitle()}</span>
                <span className={classNames(styles.arrowIcon, isOpen && styles.arrowUp)}>
                  <ArrowDownIcon width={12} height={5}/>
                </span>
              </div>
            </div>
          )}
        />
      </div>
      {filteredSchedulesGroups.length === 0 && (
        <div className={styles.notFound}>К сожалению, на эту дату сеансов не найдено.</div>
      )}
      {filteredSchedulesGroups.length > 0 && filteredSchedulesGroups.map((scheduleGroup, index) => {
        return (
          <MovieScheduleRow
            key={`${scheduleGroup.event._id}_${filterDates.start}_${index}`}
            scheduleGroup={scheduleGroup}
            movie={movie}
            filterDates={filterDates}
          />
        )
      })}
    </div>
  )
}
